import React, { useEffect, useState } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import SurveyApiService from "../../services/SurveyApiService";
import { SURVEY_API_ENDPOINT, SCALE_OPTIONS } from "../../constants";
import "../../styles/PublicSurvey.scss";

export default function PublicSurvey() {
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const categoryId = searchParams.get("category_id");
  const subCategoryId = searchParams.get("sub_category_id");
  const subSubCategoryId = searchParams.get("sub_sub_category_id");
  const formId = searchParams.get("form_id");

  const [surveyData, setSurveyData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [showErrorBanner, setShowErrorBanner] = useState(false);

  // Cache of scale-point-count -> option labels, fetched from
  // SURVEY_API_ENDPOINT.surveyQuestionScaleOptions(scale). Falls back to the
  // hardcoded 5-point SCALE_OPTIONS whenever a question's configured scale
  // hasn't been fetched (or the question has no scale override at all).
  const [scaleOptionsCache, setScaleOptionsCache] = useState({});

  // Dynamic Survey Settings State (Plants, Gender, Age Scale, Designation, Department, Education)
  const [settings, setSettings] = useState({
    plants: [],
    genderSetting: { is_male: true, is_female: true, is_others: false },
    ageScale: null,
    designations: [],
    departments: [],
    educations: [],
  });

  // Basic Details Form State
  const [plant, setPlant] = useState("");
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("");
  const [designation, setDesignation] = useState("");
  const [department, setDepartment] = useState("");
  const [education, setEducation] = useState("");
  const [detailsErrors, setDetailsErrors] = useState({});

  const clearFieldError = (fieldName) => {
    if (detailsErrors[fieldName]) {
      setDetailsErrors((prev) => {
        const updated = { ...prev };
        delete updated[fieldName];
        return updated;
      });
    }
  };

  const handlePlantChange = (e) => {
    setPlant(e.target.value);
    clearFieldError("plant");
  };

  const handleAgeChange = (e) => {
    setAge(e.target.value);
    clearFieldError("age");
  };

  const handleGenderChange = (e) => {
    setGender(e.target.value);
    clearFieldError("gender");
  };

  const handleDesignationChange = (e) => {
    setDesignation(e.target.value);
    clearFieldError("designation");
  };

  const handleDepartmentChange = (e) => {
    setDepartment(e.target.value);
    clearFieldError("department");
  };

  const handleEducationChange = (e) => {
    setEducation(e.target.value);
    clearFieldError("education");
  };

  const getNormalizedParts = (data) => {
    if (!data) return [];

    const getQuestionsList = (src) => {
      if (!src) return [];
      if (Array.isArray(src)) return src;
      return (
        Array.isArray(src.questions) ? src.questions :
        Array.isArray(src.items) ? src.items :
        Array.isArray(src.uploaded_questions) ? src.uploaded_questions :
        Array.isArray(src.survey_questions) ? src.survey_questions :
        Array.isArray(src.uploaded_ques) ? src.uploaded_ques :
        Array.isArray(src.all_questions) ? src.all_questions :
        Array.isArray(src.ques) ? src.ques :
        Array.isArray(src.data) ? src.data :
        Array.isArray(src.data?.questions) ? src.data.questions :
        []
      );
    };

    if (Array.isArray(data)) {
      return [{ id: "part-1", name: "Survey Questions", sections: [{ id: "sec-1", title: "", questions: data }] }];
    }

    const topQuestions = [
      ...getQuestionsList(data),
      ...(Array.isArray(data.questions) ? data.questions : []),
      ...(Array.isArray(data.all_questions) ? data.all_questions : []),
      ...(Array.isArray(data.survey_questions) ? data.survey_questions : []),
      ...(Array.isArray(data.uploaded_questions) ? data.uploaded_questions : []),
      ...(Array.isArray(data.uploaded_ques) ? data.uploaded_ques : []),
      ...(Array.isArray(data.items) ? data.items : []),
      ...(Array.isArray(data.data?.questions) ? data.data.questions : []),
      ...(Array.isArray(data.data?.survey_questions) ? data.data.survey_questions : []),
    ];

    const matchQuestionToSection = (q, section) => {
      if (!q || !section) return false;
      const qSecId = q.section_id || q.sectionId;
      const qSecTitle = (q.section_title || q.sectionTitle || q.section_name || q.sectionName || "").trim().toLowerCase();
      const secId = String(section.id || section.section_id || section._id || "");
      const secTitle = String(section.title || section.name || section.section_name || "").trim().toLowerCase();

      if (qSecId != null && qSecId !== "" && secId) {
        if (String(qSecId) === secId) return true;
      }
      if (qSecTitle && secTitle) {
        if (qSecTitle === secTitle) return true;
      }
      if (qSecId != null && qSecId !== "" && secTitle) {
        if (String(qSecId).trim().toLowerCase() === secTitle) return true;
      }
      return false;
    };

    const rawParts = Array.isArray(data.parts)
      ? data.parts
      : Array.isArray(data?.data?.parts)
      ? data.data.parts
      : Array.isArray(data?.survey_parts)
      ? data.survey_parts
      : null;

    if (Array.isArray(rawParts) && rawParts.length > 0) {
      return rawParts.map((part, pIdx) => {
        const partId = part.id || part.part_id || part.partId || (pIdx + 1);
        const partName = part.name || part.part_name || `Part ${pIdx + 1}`;

        const rawSections = Array.isArray(part.sections)
          ? part.sections
          : Array.isArray(part.data?.sections)
          ? part.data.sections
          : [];

        const partQuestions = [
          ...getQuestionsList(part),
          ...(Array.isArray(part.questions) ? part.questions : []),
          ...(Array.isArray(part.items) ? part.items : []),
          ...(Array.isArray(part.survey_questions) ? part.survey_questions : []),
          ...topQuestions.filter((q) => {
            const qPartId = q.part_id || q.partId;
            const qPartName = (q.part_name || q.partName || q.part || "").trim().toLowerCase();
            if (qPartId != null && qPartId !== "" && String(qPartId) === String(partId)) return true;
            if (qPartName && partName.trim().toLowerCase() === qPartName) return true;
            return false;
          }),
        ];

        const seenQuestionKeys = new Set();

        const sections = rawSections.map((sec, sIdx) => {
          const secDirectQuestions = getQuestionsList(sec);
          const matchedFromPart = partQuestions.filter((q) => matchQuestionToSection(q, sec));
          const matchedFromTop = topQuestions.filter((q) => matchQuestionToSection(q, sec));

          const allSecQ = [...secDirectQuestions, ...matchedFromPart, ...matchedFromTop];
          const uniqueSecQ = [];

          allSecQ.forEach((q) => {
            if (!q) return;
            const qKey = String(q.id || q.question_id || q._id || q.question_text || q.text);
            if (!seenQuestionKeys.has(qKey)) {
              seenQuestionKeys.add(qKey);
              uniqueSecQ.push(q);
            }
          });

          return {
            ...sec,
            id: sec.id || sec.section_id || sec._id || `sec-${pIdx}-${sIdx}`,
            title: sec.title || sec.name || sec.section_name || `Section ${sIdx + 1}`,
            description: sec.description || "",
            questions: uniqueSecQ,
          };
        });

        // Unassigned questions for this part
        const unassignedQuestions = partQuestions.filter((q) => {
          if (!q) return false;
          const qKey = String(q.id || q.question_id || q._id || q.question_text || q.text);
          return !seenQuestionKeys.has(qKey);
        });

        if (unassignedQuestions.length > 0) {
          const uniqueUnassigned = [];
          unassignedQuestions.forEach((q) => {
            const qKey = String(q.id || q.question_id || q._id || q.question_text || q.text);
            if (!seenQuestionKeys.has(qKey)) {
              seenQuestionKeys.add(qKey);
              uniqueUnassigned.push(q);
            }
          });

          if (uniqueUnassigned.length > 0) {
            if (sections.length === 0) {
              sections.push({
                id: `part-${partId}-general`,
                title: "",
                description: "",
                questions: uniqueUnassigned,
              });
            } else {
              sections[0].questions = [...sections[0].questions, ...uniqueUnassigned];
            }
          }
        }

        return {
          ...part,
          id: partId,
          name: partName,
          sections,
        };
      });
    }

    const rawSections = Array.isArray(data.sections) ? data.sections : [];
    if (rawSections.length > 0) {
      const seenQ = new Set();
      const sections = rawSections.map((sec, sIdx) => {
        const secDirectQuestions = getQuestionsList(sec);
        const matchedFromTop = topQuestions.filter((q) => matchQuestionToSection(q, sec));
        const allQ = [...secDirectQuestions, ...matchedFromTop];
        const uniqueQ = [];
        allQ.forEach((q) => {
          if (!q) return;
          const qKey = String(q.id || q.question_id || q._id || q.question_text || q.text);
          if (!seenQ.has(qKey)) {
            seenQ.add(qKey);
            uniqueQ.push(q);
          }
        });
        return {
          ...sec,
          id: sec.id || sec.section_id || sec._id || `sec-fb-${sIdx}`,
          title: sec.title || sec.name || sec.section_name || `Section ${sIdx + 1}`,
          questions: uniqueQ,
        };
      });
      return [{ id: "part-1", name: "Survey Questions", sections }];
    }

    if (topQuestions.length > 0) {
      return [{ id: "part-1", name: "Survey Questions", sections: [{ id: "sec-1", title: "", questions: topQuestions }] }];
    }

    return [];
  };

  // Which scale (number of points, e.g. 3 or 5) a given question is configured
  // to use. Adjust/extend this field list if your question payload uses a
  // different key.
  const getQuestionScale = (q) => {
    const raw =
      q?.scale ??
      q?.scale_type ??
      q?.scaleType ??
      q?.no_of_scale ??
      q?.rating_scale ??
      q?.number_of_options ??
      q?.no_of_options ??
      null;
    const num = Number(raw);
    return Number.isFinite(num) && num > 0 ? num : null;
  };

  // Resolve the option labels a question should render: use the options
  // embedded directly on the question (the API returns these alongside
  // "scale", e.g. scale: 3 -> options: ["Disagree","Neutral","Agree"]).
  // Fall back to a fetched scale-options-cache entry, then to the default
  // 5-point SCALE_OPTIONS only if neither is available.
  const getOptionsForQuestion = (q) => {
    if (Array.isArray(q?.options) && q.options.length > 0) {
      return q.options.map((o) => (typeof o === "string" ? o : o?.label || o?.name || o?.option || o?.value)).filter(Boolean);
    }
    const scale = getQuestionScale(q);
    if (scale && Array.isArray(scaleOptionsCache[scale]) && scaleOptionsCache[scale].length > 0) {
      return scaleOptionsCache[scale];
    }
    return SCALE_OPTIONS;
  };

  useEffect(() => {
    if (!id) return;
    const fetchSurveyData = async () => {
      setLoading(true);
      setError(null);
      try {
        const queryParams = {};
        if (categoryId) queryParams.category_id = categoryId;
        if (subCategoryId) queryParams.sub_category_id = subCategoryId;
        if (subSubCategoryId) queryParams.sub_sub_category_id = subSubCategoryId;
        if (formId) queryParams.form_id = formId;

        let res;
        try {
          res = await axios.get(SURVEY_API_ENDPOINT.publicForm(id), { params: queryParams });
        } catch (catErr) {
          res = await axios.get(SURVEY_API_ENDPOINT.publicForm(id));
        }

        if (res?.data?.status === 200 || res?.status === 200 || res?.data?.success || res?.success) {
          let data = res?.data?.data || res?.data || res;

          // If publicForm returned parts/sections but no questions attached inside them, fetch questions via API
          const initialParts = getNormalizedParts(data);
          const initialQCount = initialParts.reduce(
            (sum, p) => sum + (p.sections || []).reduce((sSum, sec) => sSum + (sec.questions?.length || 0), 0),
            0
          );

          if (initialQCount === 0) {
            try {
              const qRes = await SurveyApiService.fetchQuestions(null, null, { survey_id: id, form_id: id, ...queryParams });
              const qData = qRes?.data || qRes;
              const rawQList = Array.isArray(qRes)
                ? qRes
                : Array.isArray(qData)
                ? qData
                : Array.isArray(qData?.questions)
                ? qData.questions
                : Array.isArray(qData?.all_questions)
                ? qData.all_questions
                : Array.isArray(qData?.uploaded_questions)
                ? qData.uploaded_questions
                : Array.isArray(qData?.survey_questions)
                ? qData.survey_questions
                : [];
              if (rawQList.length > 0) {
                if (typeof data === "object" && !Array.isArray(data)) {
                  data = { ...data, questions: rawQList };
                }
              }
            } catch (qErr) {}
          }

          setSurveyData(data);

          const sObj = data?.settings || data?.demographics || data;
          const extracted = {
            plants: Array.isArray(sObj?.plants)
              ? sObj.plants.filter((p) => p.is_active !== false && p.is_deleted !== 1)
              : Array.isArray(data?.plants)
              ? data.plants.filter((p) => p.is_active !== false && p.is_deleted !== 1)
              : [],
            genderSetting:
              sObj?.gender_setting ||
              sObj?.genderSetting ||
              data?.gender_setting ||
              data?.genderSetting ||
              { is_male: true, is_female: true, is_others: false },
            ageScale:
              sObj?.age_scale ||
              sObj?.ageScale ||
              data?.age_scale ||
              data?.ageScale ||
              null,
            designations: Array.isArray(sObj?.designations)
              ? sObj.designations.filter((d) => d.is_active !== false && d.is_deleted !== 1)
              : Array.isArray(data?.designations)
              ? data.designations.filter((d) => d.is_active !== false && d.is_deleted !== 1)
              : [],
            departments: Array.isArray(sObj?.departments)
              ? sObj.departments.filter((dep) => dep.is_active !== false && dep.is_deleted !== 1)
              : Array.isArray(data?.departments)
              ? data.departments.filter((dep) => dep.is_active !== false && dep.is_deleted !== 1)
              : [],
            educations: Array.isArray(sObj?.educations)
              ? sObj.educations.filter((ed) => ed.is_active !== false && ed.is_deleted !== 1)
              : Array.isArray(data?.educations)
              ? data.educations.filter((ed) => ed.is_active !== false && ed.is_deleted !== 1)
              : [],
          };
          setSettings(extracted);
        } else {
          setError(res?.data?.msg || "Failed to load the survey. Please try again.");
        }

        try {
          const settingsRes = await SurveyApiService.fetchFullPageSettings(id, queryParams);
          const sData = settingsRes?.data || settingsRes || {};
          if (sData && (sData.plants || sData.designations || sData.departments || sData.educations || sData.genderSetting || sData.gender_setting)) {
            setSettings((prev) => ({
              plants: Array.isArray(sData.plants) && sData.plants.length > 0 ? sData.plants.filter((p) => p.is_active !== false) : prev.plants,
              genderSetting: sData.genderSetting || sData.gender_setting || sData.genderSettings || sData.gender_settings || prev.genderSetting,
              ageScale: sData.ageScale || sData.age_scale || prev.ageScale,
              designations: Array.isArray(sData.designations) && sData.designations.length > 0 ? sData.designations : prev.designations,
              departments: Array.isArray(sData.departments) && sData.departments.length > 0 ? sData.departments.filter((d) => d.is_active !== false) : prev.departments,
              educations: Array.isArray(sData.educations) && sData.educations.length > 0 ? sData.educations : prev.educations,
            }));
          }
        } catch (sErr) {}

        try {
          const gRes = await SurveyApiService.fetchGenderSettings(id);
          const gData = gRes?.data || gRes;
          if (gData && typeof gData === "object" && !Array.isArray(gData)) {
            setSettings((prev) => ({
              ...prev,
              genderSetting: {
                ...(prev.genderSetting || {}),
                ...gData,
              },
            }));
          }
        } catch (gErr) {}
      } catch (err) {
        console.error("Error fetching public survey data:", err);
        setError("Failed to load the survey.");
      } finally {
        setLoading(false);
      }
    };
    fetchSurveyData();
  }, [id]);

  // Once the survey/questions are loaded, find every distinct scale
  // (e.g. 3-point, 5-point) actually used and fetch its option labels.
  useEffect(() => {
    if (!surveyData) return;

    const scalesNeeded = new Set();
    getNormalizedParts(surveyData).forEach((part) =>
      (part.sections || []).forEach((section) =>
        (section.questions || []).forEach((q) => {
          // Skip questions that already carry their own options array —
          // nothing to fetch for those.
          if (Array.isArray(q?.options) && q.options.length > 0) return;
          const scale = getQuestionScale(q);
          if (scale) scalesNeeded.add(scale);
        })
      )
    );

    scalesNeeded.forEach((scale) => {
      if (scaleOptionsCache[scale]) return; // already fetched / fetching

      axios
        .get(SURVEY_API_ENDPOINT.surveyQuestionScaleOptions(scale))
        .then((res) => {
          const raw =
            res?.data?.data?.options ||
            res?.data?.options ||
            res?.data?.data ||
            res?.data ||
            [];
          const list = Array.isArray(raw) ? raw : [];
          const labels = list
            .map((o) => (typeof o === "string" ? o : o?.label || o?.name || o?.option || o?.value))
            .filter(Boolean);

          if (labels.length > 0) {
            setScaleOptionsCache((prev) => ({ ...prev, [scale]: labels }));
          }
        })
        .catch(() => {
          // Leave this scale unresolved; getOptionsForQuestion() will fall
          // back to the default SCALE_OPTIONS for its questions.
        });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyData]);

  const handleSelectOption = (questionId, option) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: option,
    }));
    if (validationErrors[questionId]) {
      setValidationErrors((prev) => {
        const updated = { ...prev };
        delete updated[questionId];
        return updated;
      });
    }
  };

  // Derive dynamic choices from settings
  const plantList = settings.plants || [];

  const gSet = settings.genderSetting || {};
  const isValueTrue = (val) => {
    if (val === true || val === 1) return true;
    if (typeof val === "string") {
      const lower = val.trim().toLowerCase();
      return lower === "true" || lower === "1" || lower === "yes";
    }
    return false;
  };

  const isValueFalse = (val) => {
    if (val === false || val === 0) return true;
    if (typeof val === "string") {
      const lower = val.trim().toLowerCase();
      return lower === "false" || lower === "0" || lower === "no";
    }
    return false;
  };

  const maleVal = gSet.is_male ?? gSet.isMale ?? gSet.male;
  const showMale = maleVal === undefined ? true : !isValueFalse(maleVal);

  const femaleVal = gSet.is_female ?? gSet.isFemale ?? gSet.female;
  const showFemale = femaleVal === undefined ? true : !isValueFalse(femaleVal);

  const otherVal =
    gSet.is_others ??
    gSet.is_other ??
    gSet.isOthers ??
    gSet.isOther ??
    gSet.others ??
    gSet.other ??
    gSet.show_other ??
    gSet.showOther ??
    gSet.enable_others ??
    gSet.enableOthers;

  const showOther = isValueTrue(otherVal);

  const genderOptions = [];
  if (showMale) genderOptions.push("Male");
  if (showFemale) genderOptions.push("Female");
  if (showOther) genderOptions.push("Other");
  if (genderOptions.length === 0) genderOptions.push("Male", "Female", "Other");

  const ageRangesList = settings.ageScale?.ranges && Array.isArray(settings.ageScale.ranges) ? settings.ageScale.ranges : [];
  const designationList = settings.designations || [];
  const departmentList = settings.departments || [];
  const educationList = settings.educations || [];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    // Perform validation on basic details
    const dErrors = {};
    if (plantList.length > 0 && !plant) {
      dErrors.plant = "Plant selection is required";
    }

    if (!age) {
      dErrors.age = ageRangesList.length > 0 ? "Age range is required" : "Age is required";
    } else if (ageRangesList.length === 0 && (isNaN(age) || Number(age) <= 0 || Number(age) > 120)) {
      dErrors.age = "Please enter a valid age";
    }

    if (!gender) {
      dErrors.gender = "Gender selection is required";
    }

    if (!designation || !designation.trim()) {
      dErrors.designation = "Designation is required";
    }

    if (departmentList.length > 0 && !department) {
      dErrors.department = "Department selection is required";
    }

    if (educationList.length > 0 && !education) {
      dErrors.education = "Education qualification selection is required";
    }

    const hasDetailsErrors = Object.keys(dErrors).length > 0;
    setDetailsErrors(dErrors);

    // Perform validation on mandatory questions
    const errors = {};
    let hasErrors = false;

    const normalizedParts = getNormalizedParts(surveyData);
    if (normalizedParts.length > 0) {
      normalizedParts.forEach((part) => {
        if (part.sections) {
          part.sections.forEach((section) => {
            if (section.questions) {
              section.questions.forEach((q) => {
                const isMandatory = q.is_mandatory === 1 || q.isMandatory || q.mandatory;
                if (isMandatory && !answers[q.id]) {
                  errors[q.id] = true;
                  hasErrors = true;
                }
              });
            }
          });
        }
      });
    }

    if (hasDetailsErrors || hasErrors) {
      setValidationErrors(errors);
      setShowErrorBanner(true);

      if (hasDetailsErrors) {
        toast.error("Please fill in all required basic details.");
        const element = document.getElementById("ps-details-card");
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      } else {
        toast.error("Please answer all mandatory questions.");
        const firstErrorId = Object.keys(errors)[0];
        const element = document.getElementById(`q-card-${firstErrorId}`);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }
      return;
    }

    setSubmitting(true);
    setShowErrorBanner(false);

    try {
      // Build a quick id -> question lookup so we can score each answer
      // against the option set that question actually rendered (its
      // configured scale, if any, rather than always assuming 5 points).
      const questionsById = {};
      normalizedParts.forEach((part) =>
        (part.sections || []).forEach((section) =>
          (section.questions || []).forEach((q) => {
            questionsById[q.id] = q;
          })
        )
      );

      const payload = Object.entries(answers).map(([qId, val]) => {
        const q = questionsById[qId] || questionsById[Number(qId)];
        const opts = q ? getOptionsForQuestion(q) : SCALE_OPTIONS;
        const idx = opts.indexOf(val);
        const score = idx >= 0 ? opts.length - idx : 0;
        return {
          question_id: Number(qId),
          answer_value: val,
          answer: val,
          input_answer: val,
          score: score >= 1 && score <= opts.length ? score : 0,
        };
      });

      const responsePayload = {
        answers: payload,
        age: ageRangesList.length > 0 ? age : Number(age),
        age_range: ageRangesList.length > 0 ? age : undefined,
        gender,
        designation,
        plant,
        department,
        education,
        category_id: categoryId ? Number(categoryId) : null,
        sub_category_id: subCategoryId ? Number(subCategoryId) : null,
        sub_sub_category_id: subSubCategoryId ? Number(subSubCategoryId) : null,
        form_id: formId ? Number(formId) : null,
      };

      const res = await SurveyApiService.submitPublicSurveyResponse(id, responsePayload);

      if (res?.status === 200 || res?.data?.status === 200 || res?.success || res?.data?.success) {
        toast.success("Survey submitted successfully!");
        history.push("/thankyou/externalpage");
      } else {
        toast.error(res?.message || res?.data?.message || res?.msg || res?.data?.msg || "Submission failed. Please try again.");
      }
    } catch (err) {
      console.error("Error submitting survey response:", err);
      toast.error(err.response?.data?.message || err.response?.data?.msg || err.message || "Failed to submit response.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="ps-page">
        <div className="ps-loading-container">
          <div className="ps-spinner" />
          <p>Loading Survey...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ps-page">
        <div className="ps-container">
          <div className="ps-header-card">
            <h1>Error</h1>
            <p className="ps-description">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  const surveyTitle = surveyData?.survey_name || surveyData?.name || "Survey";
  const surveyDesc = surveyData?.instruction_cover || surveyData?.instructionCover || surveyData?.description || "";
  const coverImage = surveyData?.cover_image_url || surveyData?.coverImageUrl;

  return (
    <div className="ps-page">
      <form onSubmit={handleSubmit} className="ps-container">
        <div className="ps-header-card">
          {coverImage && (
            <img src={coverImage} alt="Survey Cover" className="ps-cover-image" />
          )}
          <h1>{surveyTitle}</h1>
          {surveyDesc && <p className="ps-description">{surveyDesc}</p>}
        </div>

        {showErrorBanner && (
          <div className="ps-error-banner">
            <span>⚠️</span>
            <span>You have missed one or more mandatory fields or questions. Please review highlighted items below.</span>
          </div>
        )}

        <div id="ps-details-card" className="ps-details-card">
          <h2>Basic Details</h2>
          <p className="ps-details-subtitle">Please provide your details before starting the survey</p>

          <div className="ps-details-grid">
            {/* 1. Plant Field (if configured) */}
            {plantList.length > 0 && (
              <div className={`ps-form-group ${detailsErrors.plant ? "ps-form-group--error" : ""}`}>
                <label htmlFor="details-plant">
                  Plant<span className="ps-mandatory-star">*</span>
                </label>
                <select id="details-plant" value={plant} onChange={handlePlantChange}>
                  <option value="">Select Plant</option>
                  {plantList.map((p) => (
                    <option key={p.id} value={p.plant_name || p.name}>
                      {p.plant_name || p.name}
                    </option>
                  ))}
                </select>
                {detailsErrors.plant && <span className="ps-field-error-text">{detailsErrors.plant}</span>}
              </div>
            )}

            {/* 2. Gender Field (configured choices) */}
            <div className={`ps-form-group ${detailsErrors.gender ? "ps-form-group--error" : ""}`}>
              <label htmlFor="details-gender">
                Gender<span className="ps-mandatory-star">*</span>
              </label>
              <select id="details-gender" value={gender} onChange={handleGenderChange}>
                <option value="">Select Gender</option>
                {genderOptions.map((gOpt) => (
                  <option key={gOpt} value={gOpt}>
                    {gOpt}
                  </option>
                ))}
              </select>
              {detailsErrors.gender && <span className="ps-field-error-text">{detailsErrors.gender}</span>}
            </div>

            {/* 3. Age Field (Age Range dropdown vs input) */}
            <div className={`ps-form-group ${detailsErrors.age ? "ps-form-group--error" : ""}`}>
              <label htmlFor="details-age">
                {ageRangesList.length > 0 ? "Age Range" : "Age"}
                <span className="ps-mandatory-star">*</span>
              </label>
              {ageRangesList.length > 0 ? (
                <select id="details-age" value={age} onChange={handleAgeChange}>
                  <option value="">Select Age Range</option>
                  {ageRangesList.map((r) => {
                    const val = r.label || r.preview || `${r.from_age} - ${r.to_age}`;
                    return (
                      <option key={r.id || r.sr_no} value={val}>
                        {val}
                      </option>
                    );
                  })}
                </select>
              ) : (
                <input
                  id="details-age"
                  type="number"
                  placeholder="Enter your age"
                  value={age}
                  onChange={handleAgeChange}
                  min="1"
                  max="120"
                />
              )}
              {detailsErrors.age && <span className="ps-field-error-text">{detailsErrors.age}</span>}
            </div>

            {/* 4. Designation Field (configured list vs text input) */}
            <div className={`ps-form-group ${detailsErrors.designation ? "ps-form-group--error" : ""}`}>
              <label htmlFor="details-designation">
                Designation<span className="ps-mandatory-star">*</span>
              </label>
              {designationList.length > 0 ? (
                <select id="details-designation" value={designation} onChange={handleDesignationChange}>
                  <option value="">Select Designation</option>
                  {designationList.map((d) => (
                    <option key={d.id} value={d.name}>
                      {d.name}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  id="details-designation"
                  type="text"
                  placeholder="Enter your designation"
                  value={designation}
                  onChange={handleDesignationChange}
                />
              )}
              {detailsErrors.designation && <span className="ps-field-error-text">{detailsErrors.designation}</span>}
            </div>

            {/* 5. Department Field (if configured) */}
            {departmentList.length > 0 && (
              <div className={`ps-form-group ${detailsErrors.department ? "ps-form-group--error" : ""}`}>
                <label htmlFor="details-department">
                  Department<span className="ps-mandatory-star">*</span>
                </label>
                <select id="details-department" value={department} onChange={handleDepartmentChange}>
                  <option value="">Select Department</option>
                  {departmentList.map((dep) => (
                    <option key={dep.id} value={dep.name}>
                      {dep.name}
                    </option>
                  ))}
                </select>
                {detailsErrors.department && <span className="ps-field-error-text">{detailsErrors.department}</span>}
              </div>
            )}

            {/* 6. Education Qualification Field (if configured) */}
            {educationList.length > 0 && (
              <div className={`ps-form-group ${detailsErrors.education ? "ps-form-group--error" : ""}`}>
                <label htmlFor="details-education">
                  Education Qualification<span className="ps-mandatory-star">*</span>
                </label>
                <select id="details-education" value={education} onChange={handleEducationChange}>
                  <option value="">Select Education Qualification</option>
                  {educationList.map((ed) => (
                    <option key={ed.id} value={ed.name}>
                      {ed.name}
                    </option>
                  ))}
                </select>
                {detailsErrors.education && <span className="ps-field-error-text">{detailsErrors.education}</span>}
              </div>
            )}
          </div>
        </div>

        {getNormalizedParts(surveyData).map((part, partIdx) => (
            <div key={part.id || partIdx} className="ps-part-section">
              <h2 className="ps-part-title">{part.name}</h2>
              {part.sections &&
                part.sections.map((section, secIdx) => (
                  <div key={section.id || secIdx} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                    {(section.title || section.description) && (
                      <div className="ps-section-card">
                        {section.title && <h3>{section.title}</h3>}
                        {section.description && <p>{section.description}</p>}
                      </div>
                    )}

                    {section.questions &&
                      section.questions.map((q, qIdx) => {
                        const isMandatory = q.is_mandatory === 1 || q.isMandatory || q.mandatory;
                        const isError = !!validationErrors[q.id];
                        return (
                          <div
                            key={q.id || qIdx}
                            id={`q-card-${q.id}`}
                            className={`ps-question-card ${isError ? "ps-question-card--error" : ""}`}
                          >
                            <div className="ps-q-header">
                              <span className="ps-q-text">{q.question_text || q.text || q.title || q.name || q.question}</span>
                              {isMandatory && (
                                <span className="ps-mandatory-badge">* Mandatory</span>
                              )}
                            </div>

                            {(q.description || q.desc) && (
                              <p className="ps-q-desc">{q.description || q.desc}</p>
                            )}

                            {(q.mediaUrl || q.media_url || q.pic) && (
                              <div className="ps-q-media">
                                {(q.mediaType?.startsWith("video/") || (q.media_url || q.pic)?.match(/\.(mp4|webm|ogg)$/i)) ? (
                                  <video src={q.mediaUrl || q.media_url || q.pic} controls />
                                ) : (
                                  <img src={q.mediaUrl || q.media_url || q.pic} alt="Question Media" />
                                )}
                              </div>
                            )}

                            <div className="ps-options-list">
                              {getOptionsForQuestion(q).map((opt) => {
                                const isSelected = answers[q.id] === opt;
                                return (
                                  <div
                                    key={opt}
                                    className={`ps-option-pill ${isSelected ? "ps-option-pill--selected" : ""}`}
                                    onClick={() => handleSelectOption(q.id, opt)}
                                  >
                                    <span>{opt}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                ))}
            </div>
          ))}

        <div className="ps-submit-container">
          <button
            type="submit"
            className="ps-btn-submit"
            disabled={submitting}
          >
            {submitting ? "Submitting..." : "Submit Survey"}
          </button>
        </div>
      </form>
    </div>
  );
}